import { useCallback, useRef } from 'react';

import { FormContextValues, WithFieldController } from '@dreryk/react-common';
import { Attachment, AttachmentsAPI, AttachmentsQrCodeUrl, AttachmentsUploader } from '@dreryk/react-components';

import { useAccountAuthorizationModal } from '~libs.desktop.shared.account';

import {
   usePatientConsentDocumentAttachmentCreateCommand,
   usePatientConsentDocumentAttachmentRemoveCommand,
} from './PatientConsentDocumentAttachmentField.hooks';
import { PatientConsentDocumentAttachmentFieldSynchronization } from './PatientConsentDocumentAttachmentFieldSynchronization';

const ATTACHMENT_FILE_NAME_MAX_CHARS = 200;
const ACCEPTED_EXTENSIONS = [
   '.pdf',
   '.p7s',
   '.p7m',
   '.xades',
   '.asice',
   '.asics',
   '.txt',
   '.rtf',
   '.csv',
   '.doc',
   '.docx',
   '.xls',
   '.xlsx',
   '.ppt',
   '.pptx',
   '.odt',
   '.ods',
   '.odp',
   '.ott',
   '.ots',
   '.pages',
   '.numbers',
   '.key',
   '.jpg',
   '.jpeg',
   '.png',
   '.gif',
   '.bmp',
   '.tif',
   '.tiff',
   '.webp',
   '.heic',
   '.heif',
   '.jp2',
   '.dcm',
   '.dicom',
   '.mp4',
   '.mov',
   '.avi',
   '.mkv',
   '.m4v',
   '.mp3',
   '.wav',
   '.m4a',
   '.ogg',
   '.zip',
   '.7z',
   '.rar',
   '.tar',
   '.gz',
];
const ATTACHMENTS_FIELD_NAME = 'attachments';

export type AttachmentsFormBeanPart = {
   [ATTACHMENTS_FIELD_NAME]: Attachment[] | null;
};

type PatientConsentDocumentAttachmentFieldProps = {
   readonly consentDocumentId: number;
   readonly qrCodeUrl: AttachmentsQrCodeUrl;
} & WithFieldController<Attachment[] | null> &
   Pick<FormContextValues<AttachmentsFormBeanPart>, 'sync'>;

export const PatientConsentDocumentAttachmentField = (
   props: PatientConsentDocumentAttachmentFieldProps
): JSX.Element => {
   const attachmentsRef = useRef<AttachmentsAPI>();
   const { openAccountAuthorizationModal, renderAccountAuthorizationnModal } = useAccountAuthorizationModal();

   const { executeAsync: createAttachment } = usePatientConsentDocumentAttachmentCreateCommand({
      consentDocumentId: props.consentDocumentId,
      onAuthorizationRequired: openAccountAuthorizationModal,
   });
   const { executeAsync: removeAttachment } = usePatientConsentDocumentAttachmentRemoveCommand();

   // eslint-disable-next-line @typescript-eslint/require-await
   const reuploadFile = useCallback(async () => {
      return attachmentsRef.current?.reuploadFile();
   }, []);

   return (
      <>
         <AttachmentsUploader
            controller={props.controller}
            uploadFile={createAttachment}
            removeFile={removeAttachment}
            attachmentsRef={attachmentsRef}
            testId={'attachments'}
         >
            <AttachmentsUploader.FileUpload
               maxFileNameLength={ATTACHMENT_FILE_NAME_MAX_CHARS}
               acceptedExtensions={ACCEPTED_EXTENSIONS}
            />
            <AttachmentsUploader.QrCode url={props.qrCodeUrl} />
         </AttachmentsUploader>
         <PatientConsentDocumentAttachmentFieldSynchronization
            consentDocumentId={props.consentDocumentId}
            sync={props.sync}
         />
         {renderAccountAuthorizationnModal({
            onAuthorization: reuploadFile,
         })}
      </>
   );
};
